import type { OSGroup } from '#dn-types/downloads.ts';

export type OSDisplay = {
  /** Human-readable OS name; falls back to the raw api slug for unrecognized values. */
  label: string;
  /** Icon/behavior group; null when the slug is unrecognized (the raw label carries it). */
  group: OSGroup | null;
};

// The detected client OS slugs (lowercased User-Agent parse) the api can produce today, labeled
// with their display names. The slug set is an open contract — the api can add slugs without a
// version bump — so anything unlisted still displays, as its raw slug.
const OS_DISPLAY_BY_METADATA: Record<string, OSDisplay | undefined> = {
  aix: { label: 'AIX', group: 'Other' },
  android: { label: 'Android', group: 'Mobile' },
  dragonfly: { label: 'DragonFly BSD', group: 'Other' },
  freebsd: { label: 'FreeBSD', group: 'Other' },
  hurd: { label: 'GNU Hurd', group: 'Other' },
  illumos: { label: 'Illumos', group: 'Other' },
  ios: { label: 'iOS', group: 'Mobile' },
  linux: { label: 'Linux', group: 'Linux' },
  macos: { label: 'macOS', group: 'macOS' },
  nacl: { label: 'Google Native Client', group: 'Other' },
  netbsd: { label: 'NetBSD', group: 'Other' },
  openbsd: { label: 'OpenBSD', group: 'Other' },
  plan9: { label: 'Plan9', group: 'Other' },
  solaris: { label: 'Solaris', group: 'Other' },
  wasm: { label: 'WebAssembly', group: 'Other' },
  windows: { label: 'Windows', group: 'Windows' },
  zos: { label: 'IBM z/OS', group: 'Other' },
};

/**
 * Map a host's detected metadata.os onto a display name + icon group. Null when the client has not
 * reported an OS — including an empty string, so an api serialization change can't silently
 * promote "unknown" to a real OS. Object.hasOwn keeps the lookup off the prototype chain, so
 * hostile-looking slugs ("constructor", "__proto__") take the raw-slug fallback like any other
 * unlisted value.
 */
export function osDisplayFromMetadata(os: string | null | undefined): OSDisplay | null {
  if (!os) {
    return null;
  }
  const display = Object.hasOwn(OS_DISPLAY_BY_METADATA, os) ? OS_DISPLAY_BY_METADATA[os] : undefined;
  return display ?? { label: os, group: null };
}
